import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiUsers,
  FiBox,
  FiDollarSign,
  FiAlertTriangle,
  FiTrendingUp,
  FiTrendingDown,
  FiShoppingCart,
  FiClock,
  FiChevronDown
} from 'react-icons/fi';
import { dashboardService } from '../../services';
import { LoadingGrid } from '../../components/UI/Loading';
import { Modal } from '../../components/UI/Modal';
import './Dashboard.scss';
import HomeWeather from './HomeWeather';

const Dashboard = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [selectedTab, setSelectedTab] = useState('clientes');
  const [showAllTopItems, setShowAllTopItems] = useState(false);

  // Datos del dashboard
  const [financialOverview, setFinancialOverview] = useState(null);
  const [topClients, setTopClients] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [salesByMonth, setSalesByMonth] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [chartMode, setChartMode] = useState('months'); // 'months' o 'years'
  const [salesByYear, setSalesByYear] = useState([]);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    loadTopData();
  }, [selectedPeriod, selectedTab]);

  useEffect(() => {
    if (selectedYear && chartMode === 'months') {
      loadSalesByMonth(selectedYear);
    }
  }, [selectedYear, chartMode]);

  const loadSalesByMonth = async (year) => {
    try {
      const salesResult = await dashboardService.getSalesByMonth({
        year,
        compare_previous_year: false
      });
      if (salesResult.success) {
        setSalesByMonth(salesResult.data?.current_year_months || []);
      }
    } catch (error) {
      console.error('Error cargando ventas por mes:', error);
    }
  };

  const loadDashboardData = async () => {
    setIsLoading(true);
    try {
      // Cargar overview financiero
      const financialResult = await dashboardService.getFinancialOverview();
      if (financialResult.success) {
        setFinancialOverview(financialResult.data);
      }

      // Cargar años con ventas disponibles
      const yearsResult = await dashboardService.getSalesByYear({
        from_year: new Date().getFullYear() - 10,
        to_year: new Date().getFullYear()
      });
      if (yearsResult.success) {
        const allYearsData = yearsResult.data?.years || [];
        const yearsWithSales = allYearsData
          .filter(y => y.total_sales > 0 || y.total_orders > 0)
          .sort((a, b) => b.year - a.year);
        setAvailableYears(yearsWithSales.length > 0 ? yearsWithSales.map(y => y.year) : [new Date().getFullYear()]);
        setSalesByYear(yearsWithSales);
      }

      // Cargar dashboard completo para obtener summary
      const dashboardResult = await dashboardService.getDashboard({ period: 'month' });
      if (dashboardResult.success) {
        setSummary(dashboardResult.data?.summary || null);
      }
    } catch (error) {
      console.error('Error cargando dashboard:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadTopData = async () => {
    try {
      if (selectedTab === 'clientes') {
        const result = await dashboardService.getTopClients({
          period: selectedPeriod,
          limit: 20
        });
        if (result.success) {
          setTopClients(result.data?.clients || []);
        }
      } else {
        const result = await dashboardService.getTopProducts({
          period: selectedPeriod,
          limit: 20
        });
        if (result.success) {
          setTopProducts(result.data?.products || []);
        }
      }
    } catch (error) {
      console.error('Error cargando top data:', error);
    }
  };

  if (isLoading) {
    return <LoadingGrid count={8} message="Cargando dashboard..." />;
  }

  const currentDate = new Date().toLocaleDateString('es-ES', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Calcular total para porcentajes
  const topData = selectedTab === 'clientes' ? topClients : topProducts;
  const totalAmount = topData.reduce((sum, item) => sum + (item.total_purchases || item.revenue || 0), 0);
  const visibleTopData = topData.slice(0, 5);

  return (
    <div className="dashboard-page">
      {/* Header de bienvenida */}
      <div className="dashboard-header dashboard-header--weather">
        <HomeWeather />
      </div>

      {/* Layout principal */}
      <div className="dashboard-main">
        {/* Columna izquierda - Deuda de clientes y órdenes */}
        <div className="left-column">
          <div className="debt-orders-section">
            <div className="section-header">
              <h2 className="section-title">DEUDA DE CLIENTES Y<br/>ÓRDENES DE COMPRA</h2>
            </div>
            <div className="debt-orders-content">
              <div className="summary-grid">
                <div className="summary-item">
                  <div className="summary-label">Facturas sin pagar</div>
                  <div className="summary-amount">
                    {dashboardService.formatCurrency(financialOverview?.unpaid_invoices?.total_amount || 0)}
                  </div>
                  <div className="summary-count">
                    {financialOverview?.unpaid_invoices?.count || 0}
                  </div>
                </div>
                <div className="summary-item summary-item--danger">
                  <div className="summary-label">Facturas vencidas</div>
                  <div className="summary-amount">
                    {dashboardService.formatCurrency(financialOverview?.overdue_invoices?.total_amount || 0)}
                  </div>
                  <div className="summary-count">
                    {financialOverview?.overdue_invoices?.count || 0}
                  </div>
                </div>
                <div className="summary-item">
                  <div className="summary-label">Órdenes de compra pendientes</div>
                  <div className="summary-amount">
                    {dashboardService.formatCurrency(financialOverview?.pending_orders?.total_amount || 0)}
                  </div>
                  <div className="summary-count">
                    {financialOverview?.pending_orders?.count || 0}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Columna derecha - Top 20 */}
        <div className="right-column">
          <div className="top-section">
            <div className="section-header">
              <h2 className="section-title">THE TOP 20</h2>
              <div className="period-selector">
                <div className="selector-dropdown" style={{ position: 'relative' }}>
                  <button
                    className="dropdown-btn"
                    onClick={() => {
                      const dropdown = document.querySelector('.dropdown-menu');
                      dropdown.style.display = dropdown.style.display === 'block' ? 'none' : 'block';
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 16px',
                      background: '#ecf0f1',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#2c3e50'
                    }}
                  >
                    {selectedTab.toUpperCase()}
                    <FiChevronDown />
                  </button>
                  <div
                    className="dropdown-menu"
                    style={{
                      display: 'none',
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      marginTop: '4px',
                      background: 'white',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      zIndex: 100,
                      minWidth: '150px'
                    }}
                  >
                    <button
                      onClick={() => {
                        setSelectedTab('clientes');
                        document.querySelector('.dropdown-menu').style.display = 'none';
                      }}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: 'none',
                        background: selectedTab === 'clientes' ? '#f0f0f0' : 'white',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#2c3e50',
                        borderRadius: '8px 8px 0 0'
                      }}
                    >
                      CLIENTES
                    </button>
                    <button
                      onClick={() => {
                        setSelectedTab('productos');
                        document.querySelector('.dropdown-menu').style.display = 'none';
                      }}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: 'none',
                        background: selectedTab === 'productos' ? '#f0f0f0' : 'white',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#2c3e50',
                        borderRadius: '0 0 8px 8px'
                      }}
                    >
                      PRODUCTOS
                    </button>
                  </div>
                </div>
                <div className="selector-options">
                  <button
                    className={`period-btn ${selectedPeriod === 'month' ? 'active' : ''}`}
                    onClick={() => setSelectedPeriod('month')}
                  >
                    MES
                  </button>
                  <button
                    className={`period-btn ${selectedPeriod === 'week' ? 'active' : ''}`}
                    onClick={() => setSelectedPeriod('week')}
                  >
                    SEMANA
                  </button>
                  <button
                    className={`period-btn ${selectedPeriod === 'quarter' ? 'active' : ''}`}
                    onClick={() => setSelectedPeriod('quarter')}
                  >
                    TRIMESTRE
                  </button>
                  <button
                    className={`period-btn ${selectedPeriod === 'year' ? 'active' : ''}`}
                    onClick={() => setSelectedPeriod('year')}
                  >
                    AÑO
                  </button>
                </div>
              </div>
            </div>

            {/* Gráfica de dona y lista */}
            <div className="top-content">
              <div className="donut-chart">
                <svg viewBox="0 0 200 200" className="donut-svg">
                  <circle cx="100" cy="100" r="80" fill="none" stroke="#e0e0e0" strokeWidth="40" />
                  {topData.slice(0, 5).map((item, index) => {
                    const percentage = dashboardService.calculatePercentage(
                      item.total_purchases || item.revenue || 0,
                      totalAmount
                    );
                    const offset = topData.slice(0, index).reduce((sum, i) =>
                      sum + dashboardService.calculatePercentage(i.total_purchases || i.revenue || 0, totalAmount), 0
                    );
                    const circumference = 2 * Math.PI * 80;
                    const strokeDasharray = `${(percentage / 100) * circumference} ${circumference}`;
                    const rotation = -90 + (offset * 3.6);

                    return (
                      <circle
                        key={index}
                        cx="100"
                        cy="100"
                        r="80"
                        fill="none"
                        stroke={dashboardService.getChartColor(index)}
                        strokeWidth="40"
                        strokeDasharray={strokeDasharray}
                        transform={`rotate(${rotation} 100 100)`}
                      />
                    );
                  })}
                </svg>
              </div>

              <div className="top-list-container">
                <div className="top-list">
                  {topData.length === 0 ? (
                    <div style={{ padding: '24px', textAlign: 'center', color: '#999', fontSize: '14px' }}>
                      Sin datos para el período seleccionado
                    </div>
                  ) : (
                    <>
                      {visibleTopData.map((item, index) => {
                        const amount = item.total_purchases || item.revenue || 0;
                        const percentage = dashboardService.calculatePercentage(amount, totalAmount);

                        return (
                          <div key={index} className="top-item">
                            <div className="item-info">
                              <div
                                className="item-color"
                                style={{ backgroundColor: dashboardService.getChartColor(index) }}
                              />
                              <div className="item-name">
                                {item.business_name || item.product_name || item.name}
                              </div>
                            </div>
                            <div className="item-stats">
                              <div className="item-value">
                                {dashboardService.formatCurrency(amount)}
                              </div>
                              <div className="item-percentage">
                                {dashboardService.formatPercentage(percentage)}
                              </div>
                            </div>
                          </div>
                        );
                      })}

                      {topData.length > 5 && (
                        <div className="see-more">
                          <button
                            type="button"
                            className="see-more-btn"
                            onClick={() => setShowAllTopItems(true)}
                          >
                            <FiChevronDown /> VER MÁS
                          </button>
                        </div>
                      )}

                      <div className="total-section">
                        <div className="total-label">TOTAL</div>
                        <div className="total-amount">
                          {dashboardService.formatCurrency(totalAmount)}
                        </div>
                        <div className="total-percentage">100%</div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={showAllTopItems}
        onClose={() => setShowAllTopItems(false)}
        title={`Ranking de ${selectedTab === 'clientes' ? 'clientes' : 'productos'} — Top ${topData.length}`}
        size="large"
      >
        <div style={{ maxHeight: 'min(65vh, 580px)', overflowY: 'auto', paddingRight: '4px' }}>
          {topData.map((item, index) => {
            const amount = item.total_purchases || item.revenue || 0;
            const percentage = dashboardService.calculatePercentage(amount, totalAmount);
            return (
              <div key={index} style={{ display: 'grid', gridTemplateColumns: '28px minmax(0, 1fr) auto auto', alignItems: 'center', gap: '12px', padding: '11px 8px', borderBottom: '1px solid #e5e7eb', fontSize: '13px' }}>
                <span style={{ color: '#6b7280', fontWeight: 600 }}>{index + 1}.</span>
                <span style={{ minWidth: 0, overflowWrap: 'anywhere', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ background: dashboardService.getChartColor(index), width: '9px', height: '9px', borderRadius: '50%', flexShrink: 0 }} />
                  {item.business_name || item.product_name || item.name}
                </span>
                <strong style={{ whiteSpace: 'nowrap' }}>{dashboardService.formatCurrency(amount)}</strong>
                <span style={{ minWidth: '45px', textAlign: 'right', color: '#6b7280' }}>{dashboardService.formatPercentage(percentage)}</span>
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '14px 8px 4px', fontWeight: 700, borderTop: '1px solid #d1d5db' }}>
          <span>TOTAL</span>
          <span>{dashboardService.formatCurrency(totalAmount)} · 100%</span>
        </div>
      </Modal>

      {/* Gráfica de ventas */}
      <div className="sales-chart">
        <div className="chart-header" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px' }}>
          <h2 className="chart-title">VENTAS</h2>
          <div className="chart-controls">
            <select
              className="year-select"
              value={chartMode === 'years' ? 'years' : 'current'}
              onChange={(e) => {
                const val = e.target.value;
                if (val === 'years') {
                  setChartMode('years');
                } else {
                  setChartMode('months');
                  setSelectedYear(new Date().getFullYear());
                }
              }}
              style={{
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '600',
                borderRadius: '8px',
                border: '2px solid #e0e0e0',
                background: 'white',
                color: '#2c3e50',
                cursor: 'pointer'
              }}
            >
              <option value="current">Año Actual</option>
              <option value="years">Años Anteriores</option>
            </select>
          </div>
        </div>
        <div className="chart-content">
          <div className="chart-bars">
            {chartMode === 'months' ? (
              (() => {
                const monthsData = salesByMonth;
                const maxSales = monthsData.length > 0
                  ? Math.max(...monthsData.map(m => m.total_sales), 1)
                  : 1;
                return monthsData.map((monthData, index) => {
                  const heightPercent = (monthData.total_sales / maxSales) * 80;
                  return (
                    <div key={monthData.month} className="year-bar">
                      <div
                        className="bar"
                        style={{
                          height: `${heightPercent}%`,
                          backgroundColor: dashboardService.getChartColor(index)
                        }}
                      >
                        <div className="bar-tooltip">
                          <div className="tooltip-year">{monthData.month_name}</div>
                          <div className="tooltip-amount">
                            {dashboardService.formatCurrency(monthData.total_sales)}
                          </div>
                          <div className="tooltip-orders">
                            {monthData.total_orders} {monthData.total_orders === 1 ? 'orden' : 'órdenes'}
                          </div>
                        </div>
                      </div>
                      <div className="year-label">{monthData.month_name}</div>
                    </div>
                  );
                });
              })()
            ) : (
              (() => {
                const yearsData = [...salesByYear].sort((a, b) => a.year - b.year);
                const maxSales = yearsData.length > 0
                  ? Math.max(...yearsData.map(y => y.total_sales), 1)
                  : 1;
                return yearsData.map((yearData, index) => {
                  const heightPercent = (yearData.total_sales / maxSales) * 80;
                  return (
                    <div
                      key={yearData.year}
                      className="year-bar"
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        setSelectedYear(yearData.year);
                        setChartMode('months');
                      }}
                    >
                      <div
                        className="bar"
                        style={{
                          height: `${heightPercent}%`,
                          backgroundColor: yearData.year === new Date().getFullYear()
                            ? '#7B2D8E'
                            : dashboardService.getChartColor(index)
                        }}
                      >
                        <div className="bar-tooltip">
                          <div className="tooltip-year">{yearData.year}</div>
                          <div className="tooltip-amount">
                            {dashboardService.formatCurrency(yearData.total_sales)}
                          </div>
                          <div className="tooltip-orders">
                            {yearData.total_orders} {yearData.total_orders === 1 ? 'orden' : 'órdenes'}
                          </div>
                        </div>
                      </div>
                      <div className="year-label">{yearData.year}</div>
                    </div>
                  );
                });
              })()
            )}
          </div>
          <div className="chart-scale">
            {(() => {
              const data = chartMode === 'months' ? salesByMonth : [...salesByYear].sort((a, b) => a.year - b.year);
              if (data.length === 0) return null;
              const maxSales = Math.max(...data.map(d => d.total_sales), 1);
              const step = maxSales / 4;
              return [
                { bottom: '80%', value: maxSales },
                { bottom: '60%', value: step * 3 },
                { bottom: '40%', value: step * 2 },
                { bottom: '20%', value: step },
                { bottom: '0%', value: 0 },
              ].map((line, i) => (
                <div key={i} className="scale-line" style={{ bottom: line.bottom }}>
                  <span className="scale-label">{dashboardService.formatCurrency(line.value)}</span>
                </div>
              ));
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
